import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Users, Edit2, UserCog } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Team {
  id: number;
  name: string;
  description?: string;
  focusArea?: string;
  department?: string;
  memberCount?: number;
  teamLeadName?: string;
  teamLeadId?: number | null;
  memberIds?: number[];
}

interface AgencyEmployee {
  id: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  position?: string | null;
  employmentType?: string | null;
}

interface TeamMembersResponse {
  teamLeadId: number | null;
  members: AgencyEmployee[];
}

const resolveMemberDisplayName = (member: AgencyEmployee): string => {
  const name = [member.firstName, member.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || member.email || "Unnamed";
};

export function TeamsTab(){
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ["/api/teams"],
    queryFn: () => fetch("/api/teams").then((r) => r.json()),
  });

  const { data: agencyEmployees = [], isLoading: agencyEmployeesLoading } = useQuery<AgencyEmployee[]>({
    queryKey: ["/api/agency/employees"],
  });

  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [manageTeamOpen, setManageTeamOpen] = useState(false);
  const [teamForManagement, setTeamForManagement] = useState<Team | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedTeamLeadId, setSelectedTeamLeadId] = useState<string | null>(null);

  const {
    data: teamMembersData,
    isLoading: teamMembersQueryLoading,
    isFetching: teamMembersQueryFetching,
  } = useQuery<TeamMembersResponse>({
    queryKey: ["team-members", teamForManagement?.id],
    enabled: manageTeamOpen && Boolean(teamForManagement?.id),
    queryFn: async ({ queryKey }) => {
      const [, teamId] = queryKey as [string, number];
      const res = await fetch(`/api/teams/${teamId}/members`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to load team members");
      }
      return (await res.json()) as TeamMembersResponse;
    },
  });

  const teamMembersLoading = teamMembersQueryLoading || teamMembersQueryFetching;

  useEffect(() => {
    if (!manageTeamOpen || !teamMembersData) {
      return;
    }
    const memberIds = teamMembersData.members.map((member) =>
      String(member.id)
    );
    setSelectedMemberIds(memberIds);
    const leadId = teamMembersData.teamLeadId;
    setSelectedTeamLeadId(
      leadId !== null && memberIds.includes(String(leadId))
        ? String(leadId)
        : null
    );
  }, [manageTeamOpen, teamMembersData]);

  useEffect(() => {
    if (
      selectedTeamLeadId &&
      !selectedMemberIds.includes(selectedTeamLeadId)
    ) {
      setSelectedTeamLeadId(null);
    }
  }, [selectedMemberIds, selectedTeamLeadId]);

  const addTeamForm = useForm({
    defaultValues: { name: "", description: "", focusArea: "", department: "" },
  });
  const editTeamForm = useForm({
    values: { name: selectedTeam?.name || "", description: selectedTeam?.description || "", focusArea: selectedTeam?.focusArea || "", department: selectedTeam?.department || "" },
  });

  const createTeamMutation = useMutation({
    mutationFn: async (newTeam: any) => await apiRequest('POST', '/api/teams', newTeam),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      toast({ title: "Team created", description: "New team has been successfully created." , variant: "success"});
      setAddTeamOpen(false);
      addTeamForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create team. Please try again.", variant: "destructive" });
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest('PATCH', `/api/teams/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      toast({ title: "Team updated", description: "Team information has been successfully updated." , variant: "success"});
      setEditTeamOpen(false);
      setSelectedTeam(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update team. Please try again.", variant: "destructive" });
    },
  });

  const manageTeamMutation = useMutation({
    mutationFn: async ({
      teamId,
      memberIds,
      teamLeadId,
    }: {
      teamId: number;
      memberIds: number[];
      teamLeadId: number | null;
    }) => {
      const res = await apiRequest("PATCH", `/api/teams/${teamId}`, {
        memberIds,
        teamLeadId,
      });
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/teams"] });
      queryClient.invalidateQueries({
        queryKey: ["team-members", variables.teamId],
      });
      toast({
        title: "Team updated",
        description: "Team membership has been updated.",
        variant: "success",
      });
      setManageTeamOpen(false);
      setTeamForManagement(null);
      setSelectedMemberIds([]);
      setSelectedTeamLeadId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.message || "Failed to update team membership.",
        variant: "destructive",
      });
    },
  });

  const handleAddTeam = (data: any) => createTeamMutation.mutate(data);
  const handleEditTeam = (data: any) => updateTeamMutation.mutate({ id: (selectedTeam as any).id, data });
  const openEditTeam = (team: Team) => { setSelectedTeam(team); setEditTeamOpen(true); };
  const openManageTeam = (team: Team) => {
    setTeamForManagement(team);
    setSelectedMemberIds([]);
    setSelectedTeamLeadId(null);
    setManageTeamOpen(true);
  };

  const handleManageDialogChange = (open: boolean) => {
    setManageTeamOpen(open);
    if (!open) {
      setTeamForManagement(null);
      setSelectedMemberIds([]);
      setSelectedTeamLeadId(null);
    }
  };

  const handleToggleMember = (
    memberId: string,
    value: boolean | "indeterminate"
  ) => {
    const isChecked = value === true;
    setSelectedMemberIds((prev) => {
      if (isChecked) {
        if (prev.includes(memberId)) {
          return prev;
        }
        return [...prev, memberId];
      }
      return prev.filter((id) => id !== memberId);
    });
    if (!isChecked && selectedTeamLeadId === memberId) {
      setSelectedTeamLeadId(null);
    }
  };

  const handleTeamLeadSelect = (value: string) => {
    if (value === "none") {
      setSelectedTeamLeadId(null);
    } else {
      setSelectedTeamLeadId(value);
    }
  };

  const handleManageTeamSave = () => {
    if (!teamForManagement || manageTeamMutation.isPending) {
      return;
    }
    const memberIdNumbers = selectedMemberIds.map((id) => Number(id));
    if (memberIdNumbers.some((id) => Number.isNaN(id))) {
      toast({
        title: "Error",
        description: "One or more selected members are invalid.",
        variant: "destructive",
      });
      return;
    }
    const teamLeadId =
      selectedTeamLeadId && selectedMemberIds.includes(selectedTeamLeadId)
        ? Number(selectedTeamLeadId)
        : null;
    if (teamLeadId !== null && Number.isNaN(teamLeadId)) {
      toast({
        title: "Error",
        description: "Invalid team lead selection.",
        variant: "destructive",
      });
      return;
    }
    manageTeamMutation.mutate({
      teamId: teamForManagement.id,
      memberIds: memberIdNumbers,
      teamLeadId,
    });
  };

  const selectionLoading = agencyEmployeesLoading || teamMembersLoading;
  const combinedMembers = useMemo(() => {
    const memberMap = new Map<string, AgencyEmployee>();
    for (const member of agencyEmployees) {
      memberMap.set(String(member.id), member);
    }
    if (teamMembersData) {
      for (const member of teamMembersData.members) {
        const key = String(member.id);
        if (!memberMap.has(key)) {
          memberMap.set(key, member);
        }
      }
    }
    return Array.from(memberMap.values()).sort((a, b) =>
      resolveMemberDisplayName(a)
        .toLowerCase()
        .localeCompare(resolveMemberDisplayName(b).toLowerCase())
    );
  }, [agencyEmployees, teamMembersData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Teams & Departments</h2>
          <p className="text-muted-foreground">
            Group agency staff into working units. Assign a team to a client or
            prospect (on their profile) and that team's members share the
            account — including its team-scoped tasks.
          </p>
        </div>
        <Dialog open={addTeamOpen} onOpenChange={setAddTeamOpen}>
          <DialogTrigger asChild>
            <Button>
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Team</DialogTitle>
              <DialogDescription>
                A team is a group of agency staff who work an account together.
                After creating one, add members below and assign the team to
                clients or prospects from their profile — team members then see
                that account's team-scoped tasks and appear as its working group.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={addTeamForm.handleSubmit(handleAddTeam)} className="space-y-4">
              <div>
                <Label htmlFor="teamName">
                  Team Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="teamName"
                  {...addTeamForm.register("name", { required: "Team name is required" })}
                  placeholder="e.g., Sales Team"
                  className={addTeamForm.formState.errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {addTeamForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-red-600">
                    {String(addTeamForm.formState.errors.name.message)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="teamDescription">Description</Label>
                <Textarea id="teamDescription" {...addTeamForm.register("description")} placeholder="Brief description of the team's purpose" rows={3} />
              </div>
              <div>
                <Label htmlFor="focusArea">Focus Area</Label>
                <Input id="focusArea" {...addTeamForm.register("focusArea")} placeholder="e.g., New Business Acquisition" />
              </div>
              <div>
                <Label htmlFor="department">Department</Label>
                <Controller
                  control={addTeamForm.control}
                  name="department"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Claims">Claims</SelectItem>
                        <SelectItem value="Client Services">Client Services</SelectItem>
                        <SelectItem value="Underwriting">Underwriting</SelectItem>
                        <SelectItem value="Administration">Administration</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setAddTeamOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Team</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {teamsLoading ? (
          <div className="col-span-2 text-center py-8">Loading teams...</div>
        ) : teams.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">No teams created yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Get started by creating your first team to organize your agency employees.</p>
            <Button onClick={() => setAddTeamOpen(true)}>
              Create Your First Team
            </Button>
          </div>
        ) : (
          teams.map((team: any) => (
            <Card key={team.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="h-5 w-5" />
                    <span>{team.name}</span>
                  </CardTitle>
                  <Badge variant="secondary">{team.memberCount || 0} members</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {team.description && (
                  <p className="text-sm text-muted-foreground">{team.description}</p>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Team Lead:</span>
                    <p className="text-muted-foreground">{team.teamLeadName || "Not assigned"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Focus Area:</span>
                    <p className="text-muted-foreground">{team.focusArea || "Not specified"}</p>
                  </div>
                </div>
                {team.department && (
                  <div className="text-sm">
                    <span className="font-medium">Department:</span>
                    <Badge variant="outline" className="ml-2">{team.department}</Badge>
                  </div>
                )}
                <div className="flex justify-between pt-2">
                  <Button variant="outline" size="sm" onClick={() => openEditTeam(team)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Team
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openManageTeam(team)}>
                    <UserCog className="h-4 w-4 mr-2" />
                    Manage Team
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={manageTeamOpen} onOpenChange={handleManageDialogChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Manage Team{teamForManagement ? ` • ${teamForManagement.name}` : ""}
            </DialogTitle>
            <DialogDescription>
              Add or remove agency employees from this team and assign a team lead.
            </DialogDescription>
          </DialogHeader>
          {selectionLoading ? (
            <div className="py-6 text-sm text-muted-foreground">
              Loading team details...
            </div>
          ) : combinedMembers.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground">
              No agency employees are available yet. Add employees from the Employees tab to get started.
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium">Team Members</Label>
                <ScrollArea className="mt-3 h-60 pr-2">
                  <div className="space-y-2">
                    {combinedMembers.map((member) => {
                      const memberId = String(member.id);
                      const isChecked = selectedMemberIds.includes(memberId);
                      const detailLabel =
                        member.position ||
                        member.employmentType ||
                        "Employee";
                      return (
                        <label
                          key={member.id}
                          className="flex items-start space-x-3 rounded-lg border border-border/60 p-3"
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(value) =>
                              handleToggleMember(memberId, value)
                            }
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {resolveMemberDisplayName(member)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {detailLabel}
                              {member.email ? ` • ${member.email}` : ""}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Team Lead</Label>
                <Select
                  onValueChange={handleTeamLeadSelect}
                  value={
                    selectedTeamLeadId &&
                    selectedMemberIds.includes(selectedTeamLeadId)
                      ? selectedTeamLeadId
                      : "none"
                  }
                  disabled={selectedMemberIds.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a team lead" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No team lead</SelectItem>
                    {selectedMemberIds.map((memberId) => {
                      const member = combinedMembers.find(
                        (item) => item.id === Number(memberId)
                      );
                      if (!member) return null;
                      return (
                        <SelectItem key={memberId} value={memberId}>
                          {resolveMemberDisplayName(member)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The team lead must also be part of the team members list.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleManageDialogChange(false)}
              disabled={manageTeamMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManageTeamSave}
              disabled={
                manageTeamMutation.isPending ||
                !teamForManagement ||
                selectionLoading
              }
            >
              {manageTeamMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editTeamOpen} onOpenChange={setEditTeamOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
          </DialogHeader>
          {selectedTeam && (
            <form onSubmit={editTeamForm.handleSubmit(handleEditTeam)} className="space-y-4">
              <div>
                <Label htmlFor="editTeamName">
                  Team Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="editTeamName"
                  {...editTeamForm.register("name", { required: "Team name is required" })}
                  className={editTeamForm.formState.errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {editTeamForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-red-600">
                    {String(editTeamForm.formState.errors.name.message)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="editTeamDescription">Description</Label>
                <Textarea id="editTeamDescription" {...editTeamForm.register("description")} rows={3} />
              </div>
              <div>
                <Label htmlFor="editFocusArea">Focus Area</Label>
                <Input id="editFocusArea" {...editTeamForm.register("focusArea")} />
              </div>
              <div>
                <Label htmlFor="editDepartment">Department</Label>
                <Controller
                  control={editTeamForm.control}
                  name="department"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Claims">Claims</SelectItem>
                        <SelectItem value="Client Services">Client Services</SelectItem>
                        <SelectItem value="Underwriting">Underwriting</SelectItem>
                        <SelectItem value="Administration">Administration</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditTeamOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
